import React from 'react';
import { ReactComponent as CustomFormulaIcon } from 'src/icons/customFormula.svg';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import kipifyLogo from 'src/icons/kipify-logo.png';
import {
  ConnectorType,
  CustomFormulaItem,
  CustomFormulaItemType,
  Metric,
  MetricFilterItem,
  MetricsByConnectorType,
  RoleOr,
} from 'src/utils/gql/types';
import { v4 as uuidv4 } from 'uuid';

export interface FormattedCustomFormulaItem {
  label: string;
  value: string;
  type: CustomFormulaItemType;
  width: number;
  id: string;
  metricId?: string;
  connectorType?: ConnectorType | MetricsByConnectorType;
  filters?: (MetricFilterItem | { filtersGroup: MetricFilterItem[] })[];
}

export const getIconForMetric = (
  type?: ConnectorType | MetricsByConnectorType,
) => {
  if (!type) return;

  switch (type) {
    case ConnectorType.SHOPIFY:
      return <ShopifyIcon />;
    case ConnectorType.GOOGLE_ADS:
      return <GoogleAdsIcon />;
    case ConnectorType.GOOGlE_ANALYTICS4:
      return <GoogleAnalyticsIcon />;
    case ConnectorType.GOOGLE_SEARCH_CONSOLE:
      return <GoogleSearchConsoleIcon />;
    case ConnectorType.FACEBOOK:
      return <FacebookAdsIcon />;
    case ConnectorType.DATABASE:
      return (
        <div
          style={{
            width: '20px',
            height: '20px',
            backgroundColor: '#1976d2',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          DB
        </div>
      );
    case ConnectorType.MAGENTO:
      return (
        <div
          style={{
            width: '20px',
            height: '20px',
            backgroundColor: '#ee6723',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          M
        </div>
      );
    case ConnectorType.KIPIFY:
    case MetricsByConnectorType.KIPIFY:
      // Usa la stessa icona di MetricSelector
      return (
        <img
          src={kipifyLogo}
          alt="Kipify"
          style={{
            width: '20px',
            height: '20px',
            objectFit: 'contain',
          }}
        />
      );
    case MetricsByConnectorType.CUSTOM_FORMULA:
      return <CustomFormulaIcon />;
  }
};

export const trimWhitespaces = (value: string) =>
  value.trimStart().replace(/\s\s+/g, ' ');

export const formatCustomFormulaItems = (
  customFormulaItems: CustomFormulaItem[],
): FormattedCustomFormulaItem[] => {
  return customFormulaItems.map((item) => {
    const filter = item.filters?.[0];

    const formattedFilters =
      filter?.filterGroupOr?.reduce(
        (
          acc: Array<MetricFilterItem | { filtersGroup: MetricFilterItem[] }>,
          { rulesAnd },
        ) => {
          if (rulesAnd.length > 1) {
            acc.push({
              filtersGroup: rulesAnd.map(({ operator, values }) => ({
                key: filter.key,
                operator,
                values,
              })),
            });
          } else {
            const [firstRule] = rulesAnd;

            if (firstRule) {
              acc.push({
                key: filter.key,
                operator: firstRule.operator,
                values: firstRule.values,
              });
            }
          }

          return acc;
        },
        [],
      ) ?? [];

    return {
      ...item,
      width: 0,
      id: uuidv4(),
      filters: formattedFilters,
    };
  });
};

export const unformatCustomFormulaItems = (
  formattedItems: FormattedCustomFormulaItem[],
): CustomFormulaItem[] => {
  return formattedItems.map((item) => {
    let key = '';

    const filterGroupOr: RoleOr[] =
      item.filters?.map((nestedFilters): RoleOr => {
        if ('filtersGroup' in nestedFilters) {
          return {
            rulesAnd: nestedFilters.filtersGroup.map(
              ({ key: currentKey, operator, values }) => {
                key = key || currentKey;

                return {
                  operator,
                  values: [...values],
                };
              },
            ),
          };
        } else {
          key = key || nestedFilters.key;

          return {
            rulesAnd: [
              {
                operator: nestedFilters.operator,
                values: [...nestedFilters.values],
              },
            ],
          };
        }
      }) ?? [];

    return {
      label: item.label,
      value: item.value,
      metricId: item.metricId,
      type: item.type,
      connectorType: item.connectorType,
      filters:
        key && filterGroupOr.length
          ? [
              {
                key,
                filterGroupOr,
              },
            ]
          : [],
    };
  });
};

export const groupMetricsByCategory = (
  metrics: Metric[],
): Record<string, Metric[]> => {
  const metricsByCategory: Record<string, Metric[]> = {};

  metrics.forEach((metric) => {
    if (metricsByCategory[metric.category]) {
      metricsByCategory[metric.category].push(metric);
    } else {
      metricsByCategory[metric.category] = [metric];
    }
  });

  return metricsByCategory;
};

export const isConnectorType = (value: any): value is ConnectorType => {
  return Object.values(ConnectorType).includes(value);
};

export const countFilledFilters = (
  filters: (MetricFilterItem | { filtersGroup: MetricFilterItem[] })[],
): number => {
  return filters.reduce((count, filter) => {
    if ('filtersGroup' in filter) {
      return (
        count +
        filter.filtersGroup.filter((nestedFilter) => nestedFilter.key).length
      );
    }

    return count + (filter.key ? 1 : 0);
  }, 0);
};
